import { useState, useEffect } from "react";
import { UserPlus, Loader2, Send, Pencil } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/lib/supabase";
import { useUpdateItem } from "@/hooks/useItems";
import { toast } from "sonner";
import type { Item } from "@/lib/types";
import { formatError } from "@/lib/utils";

type Props = {
  item: Item | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function DelegateSheet({ item, open, onOpenChange }: Props) {
  const [email, setEmail] = useState("");
  const [context, setContext] = useState("");
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const updateItem = useUpdateItem();

  // Sync editable fields when item changes
  useEffect(() => {
    if (item) {
      setEditTitle(item.title);
      setEditNotes(item.notes || "");
    }
  }, [item?.id]);

  if (!item) return null;

  const hasEdits =
    editTitle !== item.title || editNotes !== (item.notes || "");

  const handleSaveEdits = () => {
    const updates = {
      id: item.id,
      ...(editTitle !== item.title && { title: editTitle }),
      ...(editNotes !== (item.notes || "") && { notes: editNotes || null }),
    };
    updateItem.mutate(updates, {
      onSuccess: () => {
        toast.success("Task updated");
        setEditing(false);
      },
      onError: (err) => toast.error(formatError(err)),
    });
  };

  const handleDelegate = async () => {
    if (!email.trim()) return;

    // Save any pending edits first
    if (hasEdits) {
      updateItem.mutate({
        id: item.id,
        ...(editTitle !== item.title && { title: editTitle }),
        ...(editNotes !== (item.notes || "") && { notes: editNotes || null }),
      });
    }

    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "delegate-task",
        {
          body: {
            item_id: item.id,
            to_email: email.trim(),
            context: context.trim() || undefined,
          },
        }
      );
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success(`Delegation email sent to ${email}`);
      setEmail("");
      setContext("");
      setEditing(false);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:w-3/4 sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary" />
            Delegate Task
          </SheetTitle>
          <SheetDescription>
            Edit the task details if needed, then send a delegation email.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 mt-6">
          {/* Editable task details */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-muted-foreground text-xs">Task Details</Label>
              {!editing && (
                <button
                  onClick={() => setEditing(true)}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Pencil className="h-3 w-3" />
                  Edit
                </button>
              )}
            </div>

            {editing ? (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="edit-title" className="text-xs">
                    Title
                  </Label>
                  <Input
                    id="edit-title"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-notes" className="text-xs">
                    Notes
                  </Label>
                  <Textarea
                    id="edit-notes"
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    rows={4}
                    placeholder="Task details, context, instructions..."
                  />
                </div>
                {hasEdits && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleSaveEdits}
                    disabled={updateItem.isPending}
                  >
                    {updateItem.isPending ? (
                      <Loader2 className="h-3 w-3 animate-spin mr-1" />
                    ) : null}
                    Save Changes
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-1">
                <p className="text-sm font-medium">{editTitle}</p>
                {editNotes && (
                  <p className="text-xs text-muted-foreground whitespace-pre-line bg-muted/50 rounded-lg px-3 py-2">
                    {editNotes}
                  </p>
                )}
              </div>
            )}
          </div>

          {item.project_tag && (
            <div>
              <Label className="text-muted-foreground text-xs">Project</Label>
              <div className="mt-1">
                <Badge variant="secondary">{item.project_tag}</Badge>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="delegate-email">Send to</Label>
            <Input
              id="delegate-email"
              type="email"
              placeholder="name@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleDelegate();
                }
              }}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="delegate-context">Extra context (optional)</Label>
            <Textarea
              id="delegate-context"
              placeholder="Anything else they should know..."
              value={context}
              onChange={(e) => setContext(e.target.value)}
              rows={3}
            />
          </div>

          <Button
            onClick={handleDelegate}
            disabled={sending || !email.trim()}
            className="w-full"
          >
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            {hasEdits ? "Save & Send Delegation Email" : "Send Delegation Email"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
